import React from "react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ConversationCard } from "./conversation-card"
import { InfiniteScrollLoader } from "@/components/shared/infinite-scroll-loader"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"
import type { Mail } from "@/lib/data"
import type { ConversationStatus } from "@/lib/conversation-types"
import { useSelectedConversation } from "@/hooks/use-selected-conversation"
import { cardContieneHilo, cardLines, cardUnreadCount } from "@/lib/whatsapp-threads"
import { filasVisibles } from "@/lib/conversation-list-filter"
import { useTranslations } from "@/hooks/use-translations"

interface ConversationListProps {
  items: Mail[]
  activeTab: "active" | "archived"
  searchTerm?: string
  // Infinite scroll props
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
  translations?: {
    functions?: {
      loading?: string
      retry?: string
    }
  }
  onStatusChange?: (conversationId: string, newStatus: 'active' | 'needs_review' | 'archived' | 'completed') => void
}

export function ConversationList({
  items,
  activeTab,
  searchTerm = "",
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  onStatusChange
}: ConversationListProps) {
  const [selectedConversation, setSelectedConversation] = useSelectedConversation()
  const { t } = useTranslations()
  const areaRef = React.useRef<HTMLDivElement | null>(null)
  // El que hace scroll es el Viewport de Radix, no el Root al que llega el ref.
  const [scrollRoot, setScrollRoot] = React.useState<Element | null>(null)
  React.useEffect(() => {
    setScrollRoot(areaRef.current?.querySelector('[data-radix-scroll-area-viewport]') ?? null)
  }, [])
  const { loadingRef } = useInfiniteScroll({
    onLoadMore: onLoadMore ?? (() => {}),
    hasMore,
    loading: loadingMore,
    root: scrollRoot,
  })

  const handleSelect = (id: string) => {
    setSelectedConversation({ selected: id })
  }

  const filteredConversations = React.useMemo(
    () => filasVisibles(items, activeTab, searchTerm),
    [items, activeTab, searchTerm],
  )


  return (
    <ScrollArea ref={areaRef} className="h-full w-full">
      <div className="flex flex-col gap-2 p-4 pb-8 pt-2">
        {filteredConversations.map((item, index) => (
          <div
            key={item.id}
            // Only the first rows animate: animating every row at once promotes one
            // compositing layer per card, which starves the GPU raster budget on mobile.
            className={index < 12 ? "animate-in fade-in-0 slide-in-from-bottom-2 duration-300" : undefined}
            style={index < 12 ? {
              animationDelay: `${Math.min(index, 8) * 40}ms`, // Staggered animation (capped so late/appended rows aren't invisible)
              animationFillMode: 'both'
            } : undefined}
          >
            <ConversationCard
              id={item.id}
              title={item.subject}
              description={item.text}
              platform={item.platform}
              time={item.displayDate} // Use displayDate instead of date
              clientInfo={{
                name: item.name,
                phone: item.phone,
                email: item.email,
              }}
              status={item.status as ConversationStatus}
              isAnalyzing={item.isAnalyzing}
              onSelect={handleSelect}
              isSelected={cardContieneHilo(item, selectedConversation.selected)}
              onStatusChange={onStatusChange}
              unreadCount={cardUnreadCount(item)}
              lines={cardLines(item, t)}
            />
          </div>
        ))}

        {/* Infinite scroll loader */}
        {hasMore && (
          <InfiniteScrollLoader
            loading={loadingMore}
            hasMore={hasMore}
            ref={loadingRef as React.RefObject<HTMLDivElement>}
          />
        )}

      </div>
    </ScrollArea>
  )
}
